@if (loading()) {
  <div class="flex items-center gap-3 p-12 text-sm text-muted-foreground">
    <hlm-spinner size="20" />
    Loading reachabilities…
  </div>
}

@if (error(); as err) {
  <div class="mx-auto max-w-[800px] p-6">
    <cmt-error-state
      title="Failed to load reachabilities"
      [message]="err"
      (retry)="load()" />
  </div>
}

@if (!loading() && !error()) {
  <div class="mx-auto flex max-w-[1400px] flex-col gap-5">

    <!-- HERO CARD with filter — sticky under the 50px titlebar -->
    <div class="sticky top-[50px] z-20 rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="flex min-w-0 flex-col gap-1">
          <p class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
            Phone Service · Reachabilities
          </p>
          <h1 class="font-display text-2xl font-semibold leading-tight text-foreground">
            Reachabilities
          </h1>
        </div>
        <div class="flex flex-none items-center gap-2 pt-1">
          <button type="button" hlmBtn variant="outline" (click)="toggleAll()">
            <hlm-icon [name]="allCollapsed() ? 'lucideChevronsUpDown' : 'lucideChevronsUpDown'" size="0.9rem" />
            {{ allCollapsed() ? 'Expand all' : 'Collapse all' }}
          </button>
        </div>
      </div>

      <!-- Filter input -->
      <div class="relative mt-4">
        <hlm-icon name="lucideSearch" size="0.9rem" class="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input hlmInput
               class="w-full pl-9"
               placeholder="Filter by name, destination or type…"
               [value]="filterText()"
               (input)="filterText.set($any($event.target).value)" />
        @if (filterText()) {
          <button type="button" hlmBtn variant="ghost" size="icon"
                  class="absolute right-1 top-1/2 -translate-y-1/2"
                  (click)="filterText.set('')">
            <hlm-icon name="lucideX" size="0.9rem" />
          </button>
        }
      </div>
    </div>

    @if (filterText() && hiddenGroupCount() > 0) {
      <div class="flex items-center gap-2 rounded-lg border border-border/50 bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
        <hlm-icon name="lucideFilter" size="0.9rem" />
        {{ hiddenGroupCount() }} group{{ hiddenGroupCount() > 1 ? 's' : '' }} hidden (no matches)
      </div>
    }

    <!-- Reachability groups -->
    @for (group of groups(); track group.key) {
      @if ((filteredItemsMap().get(group.key)?.length ?? 0) > 0 || !filterText()) {
        <section [cmtRevealOnExpand]="!isCollapsed(group.key)" class="rounded-2xl border border-border bg-card shadow-sm">

          <!-- Group header (collapsible) -->
          <header class="flex cursor-pointer items-center gap-2.5 p-5 pb-0"
                  [class.pb-5]="isCollapsed(group.key)"
                  (click)="toggleSection(group.key)">
            <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
              <hlm-icon [name]="groupIcon(group)" size="1rem" />
            </span>
            <h2 class="font-display text-lg font-semibold text-foreground">{{ group.label }}</h2>
            <hlm-badge variant="secondary" class="ml-1 text-sm">{{ filteredItemsMap().get(group.key)?.length ?? 0 }}</hlm-badge>
            <span class="flex-1"></span>
            @if (!group.readOnly) {
              <button hlmBtn variant="outline" size="sm" class="h-8"
                      (click)="$event.stopPropagation(); startAdd(group)"
                      [disabled]="addingInGroup() === group.key || editingId() !== null">
                <hlm-icon name="lucidePlus" size="0.85rem" /> Add
              </button>
            }
            <hlm-icon
              [name]="isCollapsed(group.key) ? 'lucideChevronDown' : 'lucideChevronUp'"
              size="1rem"
              class="text-muted-foreground" />
          </header>

          @if (!isCollapsed(group.key)) {
            <div class="overflow-x-auto p-5 pt-4">
              @if (filteredItemsMap().get(group.key)!.length > 0 || addingInGroup() === group.key) {

                <!-- Table header -->
                <div class="mb-1 hidden items-center gap-3 border-b border-border/70 pb-2 font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground md:grid"
                     [style.grid-template-columns]="gridColumns(group)">
                  <span>Name</span>
                  <span>Destination</span>
                  @if (showTypeColumn(group)) {
                    <span>Type</span>
                  }
                  @if (isEmailGroup(group)) {
                    <span>Format</span>
                  }
                  @if (hasPublicColumn(group)) {
                    <span>Public</span>
                  }
                  @if (!group.readOnly) {
                    <span></span>
                  }
                </div>

                <!-- Data rows (capped to a scroll viewport with one-time expand) -->
                <cmt-capped-list [count]="filteredItemsMap().get(group.key)!.length">
                @for (r of filteredItemsMap().get(group.key)!; track r.id) {
                  @if (editingId() === r.id) {
                    <!-- Inline edit row -->
                    <div class="grid items-center gap-3 border-b border-border/50 py-2"
                         [style.grid-template-columns]="gridColumns(group, true)">
                      <span>
                        <input hlmInput size="sm" [formControl]="editForm.controls.name" placeholder="Name" class="w-full" />
                      </span>
                      <span>
                        <input hlmInput size="sm" [formControl]="editForm.controls.destination" placeholder="Destination" class="w-full" />
                      </span>
                      @if (showTypeColumn(group)) {
                        <span>
                          @if (hasSubTypes(group)) {
                            <hlm-select
                              [options]="typeOptionsForGroup(group)"
                              [value]="editForm.controls.type.value"
                              (valueChange)="editForm.controls.type.setValue($any($event) || defaultTypeForGroup(group))"
                            />
                          } @else {
                            <span class="text-sm text-foreground">{{ r.type }}</span>
                          }
                        </span>
                      }
                      @if (isEmailGroup(group)) {
                        <span>
                          <hlm-select
                            [options]="emailFormatSelectOptions"
                            [value]="editForm.controls.callNotificationEmailFormat.value"
                            (valueChange)="editForm.controls.callNotificationEmailFormat.setValue($any($event))"
                          />
                        </span>
                      }
                      @if (hasPublicColumn(group)) {
                        <span>
                          <hlm-switch [formControl]="editForm.controls.publiclyAvailable" aria-label="Publicly available" />
                        </span>
                      }
                      <span class="flex items-center gap-1">
                        <button type="button" hlmBtn variant="default" (click)="saveEdit()" [disabled]="editForm.invalid || saving()">
                          @if (saving()) { <hlm-spinner size="14" /> Saving… }
                          @else { <hlm-icon name="lucideSave" size="0.9rem" /> Save }
                        </button>
                        <button type="button" hlmBtn variant="outline" (click)="cancelEdit()">
                          <hlm-icon name="lucideX" size="0.9rem" />
                          Cancel
                        </button>
                      </span>
                    </div>
                  } @else {
                    <!-- Read-only row -->
                    <div class="grid items-center gap-3 rounded-md border-b border-border/50 px-2 py-3 transition-colors hover:bg-foreground/[0.04]"
                         [class.opacity-50]="isSystemBusy(r)"
                         [style.grid-template-columns]="gridColumns(group)">
                      <span class="min-w-0 truncate text-base font-medium text-foreground">{{ r.name }}</span>
                      <span class="flex min-w-0 items-center gap-1">
                        <span class="min-w-0 truncate font-mono-feat text-base text-foreground">{{ r.destination }}</span>
                        <button hlmBtn variant="ghost" size="icon" class="h-7 w-7 flex-none"
                                (click)="copyToClipboard(r.destination, $event)">
                          <hlm-icon name="lucideCopy" size="0.8rem" />
                        </button>
                      </span>
                      @if (showTypeColumn(group)) {
                        <span>
                          <hlm-badge [variant]="typeBadgeVariant(r.type)" class="text-sm">{{ r.type }}</hlm-badge>
                        </span>
                      }
                      @if (isEmailGroup(group)) {
                        <span>
                          @if (r.callNotificationEmailFormat) {
                            <hlm-badge variant="outline" class="text-sm">{{ formatLabel(r.callNotificationEmailFormat) }}</hlm-badge>
                          } @else {
                            <span class="text-base text-muted-foreground/60">—</span>
                          }
                        </span>
                      }
                      @if (hasPublicColumn(group)) {
                        <span role="img"
                              [attr.aria-label]="r.publiclyAvailable ? 'Publicly available' : 'Not publicly available'"
                              [title]="r.publiclyAvailable ? 'Publicly available' : 'Not publicly available'">
                          @if (r.publiclyAvailable) {
                            <hlm-icon name="lucideCircleCheck" size="1rem" class="text-success" />
                          } @else {
                            <hlm-icon name="lucideCircleX" size="1rem" class="text-muted-foreground/50" />
                          }
                        </span>
                      }
                      @if (!group.readOnly) {
                        <span class="flex items-center gap-1">
                          @if (canEdit(r)) {
                            <button type="button" hlmBtn variant="outline" size="sm"
                                    class="h-7 text-sm hover:bg-primary/10 hover:border-primary hover:text-primary hover:shadow-sm"
                                    (click)="startEdit(r)"
                                    [disabled]="editingId() !== null || addingInGroup() !== null">
                              <hlm-icon name="lucidePencil" size="0.75rem" />
                              Edit
                            </button>
                          }
                          @if (canDelete(r)) {
                            <button type="button" hlmBtn variant="destructive" size="sm"
                                    class="h-7 text-sm hover:bg-destructive/85 hover:shadow-md"
                                    (click)="confirmDelete(r)"
                                    [disabled]="editingId() !== null || addingInGroup() !== null">
                              <hlm-icon name="lucideTrash2" size="0.75rem" />
                              Delete
                            </button>
                          }
                        </span>
                      }
                    </div>
                  }
                }
                </cmt-capped-list>

                <!-- Add row (inline form) -->
                @if (addingInGroup() === group.key) {
                  <div class="grid items-center gap-3 border-b border-border/50 py-2"
                       [style.grid-template-columns]="gridColumns(group, true)">
                    <span>
                      <input hlmInput size="sm" [formControl]="editForm.controls.name" placeholder="Name" class="w-full" />
                    </span>
                    <span>
                      <input hlmInput size="sm" [formControl]="editForm.controls.destination" placeholder="Destination" class="w-full" />
                    </span>
                    @if (showTypeColumn(group)) {
                      <span>
                        @if (hasSubTypes(group)) {
                          <hlm-select
                            [options]="typeOptionsForGroup(group)"
                            [value]="editForm.controls.type.value"
                            (valueChange)="editForm.controls.type.setValue($any($event) || defaultTypeForGroup(group))"
                          />
                        } @else {
                          <span class="text-sm text-foreground">{{ defaultTypeForGroup(group) }}</span>
                        }
                      </span>
                    }
                    @if (isEmailGroup(group)) {
                      <span>
                        <hlm-select
                          [options]="emailFormatSelectOptions"
                          [value]="editForm.controls.callNotificationEmailFormat.value"
                          (valueChange)="editForm.controls.callNotificationEmailFormat.setValue($any($event))"
                        />
                      </span>
                    }
                    @if (hasPublicColumn(group)) {
                      <span>
                        <hlm-switch [formControl]="editForm.controls.publiclyAvailable" aria-label="Publicly available" />
                      </span>
                    }
                    <span class="flex items-center gap-1">
                      <button type="button" hlmBtn variant="default" (click)="saveNew()" [disabled]="editForm.invalid || saving()">
                        @if (saving()) { <hlm-spinner size="14" /> Saving… }
                        @else { <hlm-icon name="lucideSave" size="0.9rem" /> Save }
                      </button>
                      <button type="button" hlmBtn variant="outline" (click)="cancelEdit()">
                        <hlm-icon name="lucideX" size="0.9rem" />
                        Cancel
                      </button>
                    </span>
                  </div>
                }
              } @else {
                <div class="rounded-2xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
                  No {{ group.label.toLowerCase() }} configured
                </div>
              }
            </div>
          }
        </section>
      }
    }
  </div>
}
